import React, { useState, useRef, useEffect, useMemo } from "react";
import styled, { keyframes, css } from "styled-components";
import { IconButton } from "@material-ui/core";
import { Icon } from "js/Components/Icon";
import { VIDEO_TASK_CONFIGS } from "./taskConfig";

// Video dimensions
const VIDEO_PREVIEW_WIDTH = 300;
const VIDEO_PREVIEW_HEIGHT = 400;
const VIDEO_FULL_WIDTH = 476;
const VIDEO_FULL_HEIGHT = 560;

const FadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const FadeInPreview = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 0.8;
    }
`;

const VideoContainer = styled.div<{ $isEnlarged: boolean; $isPreview: boolean; $skipAnimation: boolean }>`
    position: fixed;
    top: 24px;
    right: 24px;
    width: ${props => props.$isEnlarged ? `${VIDEO_FULL_WIDTH}px` : `${VIDEO_PREVIEW_WIDTH}px`};
    height: ${props => props.$isEnlarged ? `${VIDEO_FULL_HEIGHT}px` : `${VIDEO_PREVIEW_HEIGHT}px`};
    z-index: ${props => props.$isPreview ? "9" : "10"};
    transition: width 0.3s ease, height 0.3s ease;
    cursor: ${props => props.$isPreview ? "default" : "pointer"};
    pointer-events: ${props => props.$isPreview ? "none" : "auto"};
    
    /* fade */
    opacity: ${props => props.$skipAnimation ? (props.$isPreview ? "0.8" : "1") : "0"};
    ${props => {
        if (props.$skipAnimation) return css`animation: none;`;
        if (props.$isPreview) return css`animation: ${FadeInPreview} 800ms ease-out forwards;`;

        // This should run whenever the video is shown for the user for the first time
        return css`animation: ${FadeIn} 600ms ease-out 800ms forwards;`;
    }}
    
    &:hover {
        width: ${props => props.$isPreview ? `${VIDEO_PREVIEW_WIDTH}px` : `${VIDEO_FULL_WIDTH}px`};
        height: ${props => props.$isPreview ? `${VIDEO_PREVIEW_HEIGHT}px` : `${VIDEO_FULL_HEIGHT}px`};
        aspect-ratio: ${VIDEO_FULL_WIDTH}/${VIDEO_FULL_HEIGHT};
    }
`;

const VideoWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 9px 37px 0 rgba(0, 0, 0, 0.20);
    background: #000;
    pointer-events: auto;
`;

const VideoElement = styled.video<{ $isPreview: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: ${props => props.$isPreview ? "brightness(0.7)" : "none"};
`;

const PlayPauseOverlay = styled.div<{ $visible: boolean }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.9);
    transition: opacity 0.3s ease, visibility 0.3s ease;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? "visible" : "hidden"};
    pointer-events: ${props => props.$visible ? "auto" : "none"};
    
    &:hover {
        background-color: rgba(255, 255, 255, 1);
    }
`;

const CloseButton = styled(IconButton)`
    &&& {
        position: absolute;
        top: 8px;
        right: 8px;
        color: #FFF;
        width: 32px;
        height: 32px;
        z-index: 10;
        background: none;
    }
`;

const TitleSubtitleOverlay = styled.div<{ $visible: boolean }>`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    z-index: 6;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    opacity: ${props => props.$visible ? 1 : 0};
    visibility: ${props => props.$visible ? "visible" : "hidden"};
    pointer-events: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Title = styled.div`
    color: #FFF;
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0.5px;
`;

const Subtitle = styled.div`
    color: #FFF;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: -0.32px;
`;

interface CoachVideoProps {
    videoUrl: string;
    taskKey: string;
    onClose?: () => void;
    onTaskComplete?: () => void;
    isPreview?: boolean;
}

const MARK_TASK_COMPLETE_TIME_IN_MS = 3000;

const CoachVideo: React.FC<CoachVideoProps> = ({ videoUrl, taskKey, onClose, onTaskComplete, isPreview = false }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isEnlarged, setIsEnlarged] = useState(false);
    const taskCompleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wasPreviewRef = useRef(isPreview);
    const [skipAnimation, setSkipAnimation] = useState(false);

    // Skip animation when transitioning from preview to non-preview
    useEffect(() => {
        if (wasPreviewRef.current && !isPreview) {
            setSkipAnimation(true);
        }
        wasPreviewRef.current = isPreview;
    }, [isPreview]);

    // Get title from taskConfig, fallback to empty string
    const { title, subtitle } = useMemo(() => {
        const config = taskKey ? VIDEO_TASK_CONFIGS[taskKey] : null;
        const { title = "", subtitle = "" } = config || {};
        return { title, subtitle };
    }, [taskKey]);

    // Handle video play/pause events
    useEffect(() => {
        const video = videoRef.current;
        if (!video || isPreview) return;

        const handlePlay = () => {
            setIsPlaying(true);
            setIsHovering(false);

            // Mark task complete after 3 seconds
            taskCompleteTimeoutRef.current = setTimeout(() => {
                onTaskComplete?.();
            }, MARK_TASK_COMPLETE_TIME_IN_MS);
        };

        const handlePause = () => {
            setIsPlaying(false);
            if (taskCompleteTimeoutRef.current) {
                clearTimeout(taskCompleteTimeoutRef.current);
                taskCompleteTimeoutRef.current = null;
            }
        };

        const handleEnded = () => {
            setIsPlaying(false);
            if (taskCompleteTimeoutRef.current) {
                clearTimeout(taskCompleteTimeoutRef.current);
                taskCompleteTimeoutRef.current = null;
            }
            // Automatically close video and open quick start menu when video ends
            handleClose();
        };

        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("ended", handleEnded);

        return () => {
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
            video.removeEventListener("ended", handleEnded);
            if (taskCompleteTimeoutRef.current) {
                clearTimeout(taskCompleteTimeoutRef.current);
            }
        };
    }, [isPreview, onTaskComplete]);

    const handlePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;

        setIsEnlarged(true);
        if (isPlaying) {
            video.pause();
        } else {
            video.play();
        }
    };

    const handleVideoClick = (e: React.MouseEvent) => {
        // Don't toggle if clicking on overlay or close button
        const target = e.target as HTMLElement;
        if (target.closest("[data-overlay]") || target.closest("[data-close-button]")) {
            return;
        }
        setIsEnlarged(true);
        handlePlayPause();
    };

    const handleClose = () => {
        const video = videoRef.current;
        if (video) {
            video.pause();
        }
        if (taskCompleteTimeoutRef.current) {
            clearTimeout(taskCompleteTimeoutRef.current);
        }
        onClose?.();
    };

    const showPlayPauseOverlay = !isPlaying || isHovering;
    const showTitleSubtitle = isPreview || !isPlaying || (isPlaying && isHovering);

    return (
        <VideoContainer
            $isEnlarged={isEnlarged}
            $isPreview={isPreview}
            $skipAnimation={skipAnimation}
            onMouseEnter={() => !isPreview && setIsHovering(true)}
            onMouseLeave={() => !isPreview && setIsHovering(false)}
        >
            <VideoWrapper onClick={isPreview ? undefined : handleVideoClick}>
                <VideoElement
                    ref={videoRef}
                    playsInline
                    $isPreview={isPreview}
                >
                    <source src={videoUrl} type="video/mp4" />
                </VideoElement>

                <TitleSubtitleOverlay $visible={showTitleSubtitle}>
                    <Title>{title}</Title>
                    <Subtitle>{subtitle}</Subtitle>
                </TitleSubtitleOverlay>

                <PlayPauseOverlay
                    data-overlay
                    $visible={showPlayPauseOverlay}
                    onClick={handlePlayPause}
                >
                    <Icon fill large color="#000">
                        {isPlaying ? "pause" : "play_arrow"}
                    </Icon>
                </PlayPauseOverlay>
                <CloseButton
                    data-close-button
                    onClick={handleClose}
                >
                    <Icon fill white medium>cancel</Icon>
                </CloseButton>

            </VideoWrapper>
        </VideoContainer>
    );
};

export default CoachVideo;

